import React, { useState, useEffect } from 'react';
import StudioBanner from '../../components/studio/StudioBanner';
import { useParams, useNavigate } from 'react-router-dom';
import { studioAdminApi as adminApi } from '../../api/studioClient';
import toast from 'react-hot-toast';
import { Plus, Layout, Pencil, Trash2, Copy, Star, X, AlertTriangle } from 'lucide-react';
import AdminPageHeader from '../../components/studio/AdminPageHeader';

export default function ScreenManager() {
  const { recordTypeId } = useParams();
  const navigate = useNavigate();
  const [screens, setScreens] = useState([]);
  const [recordType, setRecordType] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [form, setForm] = useState({ name: '', description: '', screen_type: 'detail', is_default: false });
  const [renameModal, setRenameModal] = useState(null);
  const [renameName, setRenameName] = useState('');
  const [depsModal, setDepsModal] = useState(null);

  const fetchData = async () => {
    try {
      const [screensRes, rtRes] = await Promise.all([
        adminApi.getScreens(recordTypeId),
        adminApi.getRecordType(recordTypeId),
      ]);
      setScreens(screensRes.data.screens);
      setRecordType(rtRes.data.record_type);
    } catch (err) {
      toast.error('Failed to load screens');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchData(); }, [recordTypeId]);

  const handleCreate = async () => {
    if (!form.name.trim()) { toast.error('Name is required'); return; }
    try {
      const res = await adminApi.createScreen({ ...form, record_type_id: recordTypeId });
      toast.success('Screen created');
      setShowModal(false);
      setForm({ name: '', description: '', screen_type: 'detail', is_default: false });
      navigate(`/template-studio/${recordTypeId}/screens/${res.data.screen.id}/builder`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Create failed');
    }
  };

  const handleDelete = async (screen) => {
    if (!window.confirm(`Delete "${screen.name}"?`)) return;
    try {
      await adminApi.deleteScreen(screen.id);
      toast.success('Screen deleted');
      fetchData();
    } catch (err) {
      if (err.response?.status === 409 && err.response?.data?.dependencies) {
        setDepsModal({ screen, dependencies: err.response.data.dependencies, message: err.response.data.message });
      } else {
        toast.error(err.response?.data?.error || 'Delete failed');
      }
    }
  };

  const handleDuplicate = async (screen) => {
    try {
      await adminApi.duplicateScreen(screen.id);
      toast.success('Screen duplicated');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Duplicate failed');
    }
  };

  const handleSetDefault = async (screen) => {
    try {
      await adminApi.updateScreen(screen.id, { is_default: true });
      toast.success(`"${screen.name}" is now the default screen`);
      fetchData();
    } catch (err) {
      toast.error('Failed to set default');
    }
  };

  const handleRename = async () => {
    if (!renameName.trim()) { toast.error('Name is required'); return; }
    try {
      await adminApi.updateScreen(renameModal.id, { name: renameName.trim() });
      toast.success('Screen renamed');
      setRenameModal(null);
      setRenameName('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Rename failed');
    }
  };

  return (
    <div className="max-w-4xl mx-auto">
      <StudioBanner />
      <AdminPageHeader title="Screens" description={`${recordType?.name} — design how records look`} backTo="/template-studio">
        <button onClick={() => setShowModal(true)}
          className="flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
          <Plus className="h-4 w-4" /> New Screen
        </button>
      </AdminPageHeader>

      {loading ? (
        <div className="text-center py-12 text-gray-400">Loading...</div>
      ) : screens.length === 0 ? (
        <div className="bg-white rounded-xl border border-gray-200 p-12 text-center">
          <Layout className="h-12 w-12 text-gray-300 mx-auto mb-3" />
          <h3 className="text-lg font-medium text-gray-900 mb-1">No screens yet</h3>
          <p className="text-sm text-gray-500 mb-4">Create a screen to design how records of this type are displayed and edited.</p>
          <button onClick={() => setShowModal(true)}
            className="inline-flex items-center gap-2 bg-brand-600 text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-brand-700">
            <Plus className="h-4 w-4" /> Create Screen
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {screens.map((screen) => (
            <div key={screen.id} className="bg-white rounded-xl border border-gray-200 p-4 flex items-center gap-4 hover:border-gray-300 transition-colors">
              <div className="h-10 w-10 rounded-lg bg-indigo-50 flex items-center justify-center">
                <Layout className="h-5 w-5 text-indigo-500" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-sm font-semibold text-gray-900">{screen.name}</h3>
                  {screen.is_default && (
                    <span className="text-xs bg-amber-50 text-amber-700 px-2 py-0.5 rounded-full font-medium">Default</span>
                  )}
                  <span className="text-xs bg-gray-100 text-gray-500 px-2 py-0.5 rounded-full">{screen.screen_type}</span>
                </div>
                <p className="text-xs text-gray-500">
                  {screen.description || 'No description'}
                  {screen.used_in_stages > 0 && ` · Used in ${screen.used_in_stages} workflow stage(s)`}
                </p>
              </div>
              <div className="flex items-center gap-1">
                <button onClick={() => navigate(`/template-studio/${recordTypeId}/screens/${screen.id}/builder`)}
                  className="px-3 py-1.5 text-xs font-medium text-brand-600 bg-brand-50 rounded-lg hover:bg-brand-100">
                  Edit Layout
                </button>
                {!screen.is_default && (
                  <button onClick={() => handleSetDefault(screen)} title="Set as default"
                    className="p-2 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-amber-50">
                    <Star className="h-4 w-4" />
                  </button>
                )}
                <button onClick={() => { setRenameModal(screen); setRenameName(screen.name); }} title="Rename"
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
                  <Pencil className="h-4 w-4" />
                </button>
                <button onClick={() => handleDuplicate(screen)} title="Duplicate"
                  className="p-2 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-50">
                  <Copy className="h-4 w-4" />
                </button>
                <button onClick={() => handleDelete(screen)}
                  className="p-2 text-gray-400 hover:text-red-600 rounded-lg hover:bg-red-50">
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Create Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold">New Screen</h2>
              <button onClick={() => setShowModal(false)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Screen Name</label>
                <input type="text" value={form.name} maxLength={20}
                  onChange={e => setForm({ ...form, name: e.target.value })}
                  placeholder="e.g., Main Detail View"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
                <p className="text-xs text-gray-400 mt-1 text-right">{form.name.length}/20</p>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                <textarea value={form.description}
                  onChange={e => setForm({ ...form, description: e.target.value })}
                  rows={2} placeholder="Optional"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              </div>
              <label className="flex items-center gap-2 cursor-pointer">
                <input type="checkbox" checked={form.is_default}
                  onChange={e => setForm({ ...form, is_default: e.target.checked })}
                  className="h-4 w-4 rounded border-gray-300 text-brand-600" />
                <span className="text-sm text-gray-700">Set as default screen</span>
              </label>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setShowModal(false)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleCreate}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">Create</button>
            </div>
          </div>
        </div>
      )}

      {/* Dependency Warning Modal */}
      {depsModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-md">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <div className="flex items-center gap-2">
                <AlertTriangle className="h-5 w-5 text-amber-500" />
                <h2 className="text-lg font-semibold">Cannot Delete Screen</h2>
              </div>
              <button onClick={() => setDepsModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5">
              <p className="text-sm text-gray-600 mb-3">
                <strong>"{depsModal.screen.name}"</strong> is in use and cannot be deleted. Remove it from the following first:
              </p>
              <ul className="space-y-2">
                {depsModal.dependencies.map((dep, i) => (
                  <li key={i} className="flex items-start gap-2 text-sm text-gray-700 bg-gray-50 rounded-lg px-3 py-2">
                    <Layout className="h-4 w-4 text-gray-400 mt-0.5 flex-shrink-0" />
                    <span><strong>{dep.parent.replace('Workflow: ', '')}</strong> → {dep.name.replace('Stage: ', '')}</span>
                  </li>
                ))}
              </ul>
            </div>
            <div className="flex justify-end p-5 border-t border-gray-100">
              <button onClick={() => setDepsModal(null)}
                className="px-4 py-2 bg-gray-100 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-200">Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Rename Modal */}
      {renameModal && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl w-full max-w-sm">
            <div className="flex items-center justify-between p-5 border-b border-gray-200">
              <h2 className="text-lg font-semibold">Rename Screen</h2>
              <button onClick={() => setRenameModal(null)} className="text-gray-400 hover:text-gray-600">
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-5">
              <label className="block text-sm font-medium text-gray-700 mb-1">Screen Name</label>
              <input type="text" value={renameName} maxLength={20}
                onChange={e => setRenameName(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleRename()}
                autoFocus
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500" />
              <p className="text-xs text-gray-400 mt-1 text-right">{renameName.length}/20</p>
            </div>
            <div className="flex justify-end gap-2 p-5 border-t border-gray-100">
              <button onClick={() => setRenameModal(null)} className="px-4 py-2 text-sm text-gray-600 hover:text-gray-800">Cancel</button>
              <button onClick={handleRename}
                className="px-4 py-2 bg-brand-600 text-white text-sm font-medium rounded-lg hover:bg-brand-700">Rename</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
